import { getAddress } from 'viem';
import type { Address } from '$types/address';
import { getProfileForAddress } from './envio/avatars/fetchAvatars';
import { getMetriAddress } from '$utils/getMetriAddress';
import { stateCirclesSDK } from '$lib/states/circlesSDK.svelte';
import { stateMe } from '$lib/states/me.svelte';
import type { CirclesAvatarFromEnvio } from '$types/envioTypes';
import type { Profile } from '@circles-sdk/profiles';
import { users } from '$states/users.svelte';
import { cidV0ToUint8Array } from '@circles-sdk/utils';
import { ErrorType, type AppError } from '$types/error';
import { stateError } from '$states/error.svelte';

export const fetchCirclesAvatarFromEnvio = async (
  address: Address,
): Promise<Partial<CirclesAvatarFromEnvio> | null> => {
  return await getProfileForAddress(getAddress(address));
};

/**
 * We consume legacy Profile model here:
 * @param address
 * @param profile
 */
export const updateProfile = async (address: Address, profile: Profile) => {
  if (!profile.name) {
    throw new Error('Profile name can not be empty.');
  }

  if (!address) {
    throw new Error('Address not found.');
  }

  const sdk = stateCirclesSDK.current;

  if (!sdk) {
    throw new Error('SDK not initialized.');
  }

  return await sdk.createOrUpdateProfile(profile);
};

/**
 * We consume legacy Profile model here:
 * @param address
 * @param profile
 */
export const createProfileBeforeCirclesAccount = async (address: Address, profile: Profile) => {
  if (!profile.name) {
    throw new Error('Profile name can not be empty.');
  }

  try {
    await stateCirclesSDK.load(address);
    const sdk = stateCirclesSDK.current;

    return await sdk.createOrUpdateProfile(profile);
  } catch (error) {
    //notallowed error is thrown when passkey is cancelled from circles sdk
    stateError.set({
      type: ErrorType.SILENT,
      rawError: error as AppError,
      message: `${error}`,
      component: 'createProfileBeforeCirclesAccount',
    });
    throw 'Transaction canceled';
  }
};

export const loadProfile = async (address?: Address) => {
  const me = (await users.byAddress(address ?? getMetriAddress())).current;

  const name = me.name ?? window.localStorage.getItem('temp_name');

  /**
   * This means we set stateMe value during onboarding, and profile data is not yet available.
   * In that case, just skip and stick to the fixed value.
   */
  if (stateMe.current?.name && !name) {
    return;
  }

  if (me.name) {
    window.localStorage.removeItem('temp_name');
  }

  stateMe.set({
    ...me,
    name,
  });
};

export const cidV0ToMetadataDigest = async (cidV0: string) => {
  if (typeof cidV0 === 'string') {
    if (!cidV0.startsWith('Qm')) {
      throw new Error('Invalid profile CID. Must be a CIDv0 string (Qm...).');
    }

    const uint8Array = cidV0ToUint8Array(cidV0);
    const hexString =
      '0x' +
      Array.from(uint8Array)
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');

    return hexString;
  }
};
